/** A selector taking arguments. */
export type CurriedAppSelector<
  State,
  Result,
  Params extends readonly unknown[],
> = (...args: Params) => (state: State) => Result;

/**
 * Transform a selector which takes multiple arguments into a version which first takes the arguments and then only the state.
 *
 * Use this function in combination with `reselect` to create selectors which are compatible with `useAppSelector`.
 *
 * @param selector The selector to curry
 * @returns The curried selector
 * @see https://reselect.js.org/FAQ#how-can-i-make-a-curried-selector
 */
export function curryAppSelector<
  State,
  Result,
  Params extends readonly unknown[],
  AdditionalFields,
>(
  selector: ((state: State, ...args: Params) => Result) & AdditionalFields,
): CurriedAppSelector<State, Result, Params> & AdditionalFields {
  // eslint-disable-next-line func-style -- FIXME
  const curriedSelector =
    (...args: Params) =>
    (state: State) =>
      selector(state, ...args);
  return Object.assign(curriedSelector, selector);
}
